import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowPathIcon, 
  XMarkIcon,
  SparklesIcon 
} from '@heroicons/react/24/outline';
import { usePWA } from '../../contexts/PWAContext';
import { Button, Card, H3, Body } from '../ui';

interface UpdatePromptProps {
  onClose?: () => void;
  variant?: 'banner' | 'modal' | 'card';
}

const UpdatePrompt: React.FC<UpdatePromptProps> = ({ 
  onClose, 
  variant = 'banner' 
}) => {
  const { hasUpdate, updateApp } = usePWA();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      await updateApp();
      setIsVisible(false);
      onClose?.();
    } catch (error) {
      console.error('Update failed:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  if (!hasUpdate || !isVisible) {
    return null;
  }

  const content = (
    <div className="flex items-center justify-between p-4">
      <div className="flex items-center space-x-3">
        <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
          <SparklesIcon className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <H3 className="text-sm font-medium">Update Available</H3>
          <Body className="text-xs text-gray-600 dark:text-gray-400">
            A new version of Sprout is ready to install
          </Body>
        </div>
      </div>
      
      <div className="flex items-center space-x-2">
        <Button
          variant="primary"
          size="sm"
          onClick={handleUpdate}
          disabled={isUpdating}
          isLoading={isUpdating}
          className="flex items-center space-x-2"
        >
          <ArrowPathIcon className="w-4 h-4" />
          <span>Update</span>
        </Button>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClose}
          className="p-2"
        >
          <XMarkIcon className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  if (variant === 'banner') {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed top-0 left-0 right-0 z-50 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800"
        >
          {content}
        </motion.div>
      </AnimatePresence>
    );
  }

  if (variant === 'card') {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ x: 300, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 300, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed top-4 right-4 z-50 max-w-sm"
        >
          <Card className="border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
            {content}
          </Card>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Modal variant
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="max-w-md w-full mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <Card className="p-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                <SparklesIcon className="w-8 h-8 text-green-600" />
              </div>
              
              <div>
                <H3 className="mb-2">Update Available</H3>
                <Body className="text-gray-600 dark:text-gray-400">
                  A new version of Sprout is available with improvements and bug fixes. 
                  Update now to get the latest features.
                </Body>
              </div>
              
              <div className="flex space-x-3">
                <Button
                  variant="primary"
                  onClick={handleUpdate}
                  disabled={isUpdating}
                  isLoading={isUpdating}
                  className="flex-1"
                >
                  Update Now
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleClose}
                  className="flex-1"
                >
                  Later
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UpdatePrompt;
